





































































































































import {
  defineComponent, onMounted, provide, Ref, ref, nextTick, watch, PropType, computed
} from '@nuxtjs/composition-api';

import ClientOnly from 'vue-client-only';
import Teleport from 'vue2-teleport';
import SkeletonLoader from '~/components/SkeletonLoader/index.vue';
import SelectedFilters from '~/modules/catalog/category/components/filters/FiltersSidebar/SelectedFilters.vue';
import CategoryModal from '../CategoryModal.vue';
import Radio from '../Sorting/Radio.vue';
import useFilters from '~/modules/catalog/category/components/filters/useFilters';
import { SortingModel } from '~/modules/catalog/category/composables/useFacet/sortingOptions';
import { useUiHelpers } from '~/composables';
import { getFilterConfig } from '~/modules/catalog/category/config/FiltersConfig';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import type { Aggregation } from '~/modules/GraphQL/types';
import type { SelectedFiltersInterface } from '~/modules/catalog/category/components/filters/useFilters';
import { categoryFiltersApplyTrack, categoryFiltersDeleteAllTrack, categorySortByTrack } from '~/helpers/trackings/24orepro/categories';

export interface UseFiltersProviderInterface {
  selectedFilters: Ref<SelectedFiltersInterface>,
  filters: Ref<Aggregation[]>,
}

export default defineComponent({
  name: 'CategoryFilters',
  components: {
    SelectedFilters,
    SkeletonLoader,
    CategoryModal,
    ClientOnly,
    Radio,
    Teleport,
    CheckboxType: () => import('./CheckboxType.vue'),
    SwatchColorType: () => import('~/modules/catalog/category/components/filters/renderer/SwatchColorType.vue'),
    RadioType: () => import('~/modules/catalog/category/components/filters/renderer/RadioType.vue'),
    YesNoType: () => import('~/modules/catalog/category/components/filters/renderer/YesNoType.vue')
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    filterCode: {
      type: String
    },
    allIds: {
      type: Array as () => string[],
    },
    filters: {
      type: Array as PropType<Aggregation[]>
    },
    totalResults: {
      type: Number
    },
    categoryName: {
      type: String
    },
    catUid: {
      type: String
    },
    term: {
      type: String
    },
    sortBy: {
      type: Object as PropType<SortingModel>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { changeFilters, clearFilters, changeSorting } = useUiHelpers();
    const removableFilters = ref([]);
    const selectedSortValue = ref("");
    const isLoading = ref(true);

    const filterButtonEl = ref<HTMLButtonElement | null>(null);
    const sortButtonEl = ref<HTMLButtonElement | null>(null);

    const {
      selectedFilters, selectFilter, removeFilter, isFilterSelected, getRemovableFilters,
    } = useFilters();

    const updateRemovableFilters = () => {
      removableFilters.value = getRemovableFilters(props.filters, selectedFilters.value);
    };

    const doSetSorting = (sort: string) => {
      changeSorting(sort, false);
      selectedSortValue.value = sort;
    };

    const selectAndApplyFilter = (filter, event) => {
      selectFilter(filter, event)
      doApplyFilters();
    };

    const doApplyFilters = () => {
      changeFilters(selectedFilters.value, false);
      updateRemovableFilters();
      if (window?.scroll) {
        window.scroll(0, 0);
      }
      categoryFiltersApplyTrack(props.categoryName, selectedFilters.value, props.filters)
      emit('reloadProducts');
      emit('close');
    };

    const doRemoveFilter = ({ id, value }: { id: string, value: string }) => {
      removeFilter(id, value);
      changeFilters(selectedFilters.value, false);
      updateRemovableFilters();
      emit('reloadProducts');
      emit('close');
    };

    const doClearFilters = () => {
      clearFilters(false);
      selectedFilters.value = {};
      updateRemovableFilters();
      categoryFiltersDeleteAllTrack(props.categoryName)
      emit('reloadProducts');
      emit('close');
    };

    watch(() => props.isVisible, (newValue) => {
      // disable Storefrontt UI's body scroll lock which is launched when :visible prop on SfSidebar changes
      // two next ticks because SfSidebar uses nextTick aswell, and we want to do something after that tick.
      if (newValue) {
        nextTick(() => nextTick(() => clearAllBodyScrollLocks()));
      }
    });

    onMounted(async () => {
      updateRemovableFilters()
      filterButtonEl.value = document.getElementById('filterModalButton') as HTMLButtonElement;
      sortButtonEl.value = document.getElementById('sortModalButton') as HTMLButtonElement;
      isLoading.value = false;
    });

    const selectedSort = computed(() => ((optVal: string) => selectedSortValue.value == optVal ? optVal : null ));
    
    const trackSelectedSort = () => {
      const selectedSort = props.sortBy.options.find(option => option.value === selectedSortValue.value)?.label || "";
      categorySortByTrack(props.categoryName, selectedSort);
    }
    
    provide('UseFiltersProvider', { isFilterSelected, selectedFilters, filters: props.filters });

    return {
      selectFilter,
      doApplyFilters,
      doRemoveFilter,
      doClearFilters,
      doSetSorting,
      getFilterConfig,
      selectAndApplyFilter,
      trackSelectedSort,
      filterButtonEl,
      sortButtonEl,
      selectedFilters,
      isLoading,
      selectedSort,
      selectedSortValue,
      removableFilters,
    };
  },

});
